<script setup>
/* global axios */
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { DirectUpload } from 'activestorage';
import { useRoute, useRouter } from 'vue-router';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import PostMediaGrid from './PostMediaGrid.vue';
import PublicationCalendar from './PublicationCalendar.vue';
import TelegramAccountComboBox from './TelegramAccountComboBox.vue';
import TelegramPostPreview from './TelegramPostPreview.vue';
import AuthenticatedMediaImage from './AuthenticatedMediaImage.vue';
import {
  CALENDAR_TIMEZONE,
  calendarDate as dateInCalendarTimezone,
  calendarDateTime,
  calendarDateTimeToIso,
  calendarRangeSignature,
  errorText,
  normalizeCalendarDate,
  normalizeCalendarView,
  normalizeGroupId,
  normalizePostSource,
  publicationEvent,
  stateLabels,
  tabs,
  uuid,
} from './model';

const route = useRoute();
const router = useRouter();
const tab = ref('calendar');
const calendarView = ref(normalizeCalendarView(route.query.view));
const calendarDate = ref(normalizeCalendarDate(route.query.date));
const calendarGroup = ref(normalizeGroupId(route.query.group));
const calendarRange = ref(null);
const calendarLoading = ref(false);
const calendarError = ref('');
const calendarTruncated = ref(false);
const sources = ref([]);
const groups = ref([]);
const calendarTimezone = computed(
  () =>
    groups.value.find(item => String(item.id) === String(calendarGroup.value))
      ?.timezone || ''
);
const calendarTimeLabel = computed(
  () =>
    groups.value.find(item => String(item.id) === String(calendarGroup.value))
      ?.time_label || 'Город и часовой пояс не определены'
);
const calendarToday = computed(() =>
  dateInCalendarTimezone(
    new Date(),
    calendarTimezone.value || CALENDAR_TIMEZONE
  )
);
const initialCalendarDateProvided = Boolean(route.query.date);
let firstTimezoneResolved = false;
const posts = ref([]);
const postCards = ref([]);
const postCardsCursor = ref(null);
const postSource = ref(normalizePostSource(route.query.post_account));
const postsLoading = ref(false);
const postsError = ref('');
const clients = ref([]);
const runs = ref([]);
const loading = ref(false);
const saving = ref(false);
const uploading = ref(false);
const denied = ref(false);
const unavailable = ref(false);
const error = ref('');
const notice = ref('');
const postDialog = ref(null);
const postPreviewDialog = ref(null);
const deletePostDialog = ref(null);
const scheduleDialog = ref(null);
const clientDialog = ref(null);
const runDialog = ref(null);
const deleteRunDialog = ref(null);
const runToDelete = ref(null);
const runDeleteError = ref('');
const deleteRunRequestKey = ref('');
const chosenPost = ref(null);
const scheduleTarget = ref(null);
const schedulePosts = ref([]);
const scheduleCursor = ref(null);
const scheduleLoading = ref(false);
const scheduleError = ref('');
const scheduleAccountReady = ref(false);
let scheduleEpoch = 0;
let scheduleRequest = null;
let scheduleSubmission = null;
const scheduleCompactOptions = computed(() =>
  schedulePosts.value.map(post => ({
    value: post.id,
    label: (post.body || post.title || 'Публикация')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 160),
    thumbnail:
      post.media?.find(item => item.kind === 'image')?.thumbnail_url || '',
    icon: post.media?.length ? 'i-lucide-paperclip' : 'i-lucide-text',
  }))
);
const previewPost = ref(null);
const postToDelete = ref(null);
const postDeleteError = ref('');
const deletingPostId = ref(null);
const deletePostRequestKey = ref('');
const selectedRun = ref(null);
const runDetail = ref(null);
const runDetailLoading = ref(false);
const runDetailError = ref('');
const runEditTime = ref('');
const runEditBody = ref('');
const runReplacement = ref('');
const replacementPosts = ref([]);
const replacementCursor = ref(null);
const replacementOpen = ref(false);
const replacementLoading = ref(false);
let runDetailEpoch = 0;
const fileInput = ref(null);
const postFiles = ref([]);
const fillSettingsDialog = ref(null);
const cancelFillDialog = ref(null);
const fillConfigured = ref(false);
const savedFillSettings = ref(null);
const fillLoading = ref(false);
const fillError = ref('');
const fillResult = ref(null);
const lastFill = ref(null);
const fillFrom = ref(
  calendarDate.value > calendarToday.value
    ? calendarDate.value
    : calendarToday.value
);
const fillForm = reactive({
  sources: [],
  mode: 'fixed',
  dailyMin: '2',
  dailyMax: '5',
  timeFrom: '10:00',
  timeTo: '21:30',
  times: '11:00, 18:00',
  days: '7',
  weekdays: [1, 2, 3, 4, 5, 6, 7],
});
const fillTimes = computed(() =>
  fillForm.times
    .split(',')
    .map(value => value.trim())
    .filter(Boolean)
);
const clockMinute = value => {
  if (!/^(1[0-9]|20|21):[0-5]\d$/.test(value) || value > '21:30') return NaN;
  const [hour, minute] = value.split(':').map(Number);
  return hour * 60 + minute;
};
const randomFillValid = computed(() => {
  const low = Number(fillForm.dailyMin);
  const high = Number(fillForm.dailyMax);
  const start = clockMinute(fillForm.timeFrom);
  const finish = clockMinute(fillForm.timeTo);
  return (
    Number.isInteger(low) &&
    Number.isInteger(high) &&
    low >= 1 &&
    high >= low &&
    high <= 12 &&
    finish >= start &&
    high <= Math.floor((finish - start) / 60) + 1
  );
});
function fillFormSettings(settings) {
  return {
    mode: settings.mode || 'fixed',
    dailyMin: String(settings.daily_min ?? 2),
    dailyMax: String(settings.daily_max ?? 5),
    timeFrom: settings.time_from || '10:00',
    timeTo: settings.time_to || '21:30',
  };
}
const fillValid = computed(
  () =>
    Boolean(calendarTimezone.value) &&
    fillForm.sources.length > 0 &&
    Number.isInteger(Number(fillForm.days)) &&
    Number(fillForm.days) >= 1 &&
    Number(fillForm.days) <= 31 &&
    fillForm.weekdays.length > 0 &&
    (fillForm.mode === 'random'
      ? randomFillValid.value
      : fillTimes.value.length > 0 &&
        fillTimes.value.length <= 12 &&
        new Set(fillTimes.value).size === fillTimes.value.length &&
        fillTimes.value.every(
          value => /^(1[0-9]|20|21):[0-5]\d$/.test(value) && value <= '21:30'
        ) &&
        fillTimes.value
          .map(value =>
            value
              .split(':')
              .reduce((hours, minute) => hours * 60 + Number(minute), 0)
          )
          .sort((a, b) => a - b)
          .every(
            (minute, index, values) =>
              !index || minute - values[index - 1] >= 60
          ))
);
const fillButtonLabel = computed(() =>
  fillConfigured.value
    ? `Заполнить ${savedFillSettings.value?.days || 7} дн. · до ${(savedFillSettings.value?.days || 7) * (savedFillSettings.value?.mode === 'random' ? savedFillSettings.value.daily_max : savedFillSettings.value?.daily_times?.length || 2)} публикаций`
    : 'Заполнить расписание'
);
let fillRequestKey = '';
let fillEpoch = 0;
let epoch = 0;
let requests = new AbortController();
let lastCalendarRangeSignature = '';
let runsEpoch = 0;
let runsRequest = null;
let activeRunsKey = '';
let completedRunsKey = '';
let postsEpoch = 0;
let postsRequest = null;

const postForm = reactive({ source: '', title: '', body: '' });
const runForm = reactive({
  post: '',
  source: '',
  group: '',
  client: '',
  runAt: '',
});
const scheduleTimezone = computed(() =>
  scheduleTarget.value
    ? scheduleTarget.value.timezone
    : groups.value.find(item => String(item.id) === String(runForm.group))
        ?.timezone || ''
);
const scheduleTimeLabel = computed(() =>
  scheduleTarget.value
    ? scheduleTarget.value.time_label
    : groups.value.find(item => String(item.id) === String(runForm.group))
        ?.time_label || 'Выберите группу с определённым городом'
);
const runTimezone = computed(
  () =>
    selectedRun.value?.group_timezone ||
    groups.value.find(item => item.id === selectedRun.value?.group_entry_id)
      ?.timezone ||
    selectedRun.value?.timezone ||
    CALENDAR_TIMEZONE
);
const runTimeLabel = computed(
  () =>
    selectedRun.value?.group_time_label ||
    groups.value.find(item => item.id === selectedRun.value?.group_entry_id)
      ?.time_label ||
    runTimezone.value
);
const clientForm = reactive({
  source: '',
  group: '',
  name: '',
  username: '',
  slots: '1',
  notes: '',
});
const sourceOptions = computed(() =>
  sources.value.map(item => ({
    value: item.id,
    label: item.name,
    username: item.username || '',
    avatarUrl: item.avatar_url || '',
    telegramName: item.telegram_name || '',
  }))
);
const postSourceOptions = computed(() => [
  {
    value: '',
    label: 'Все аккаунты',
    subtitle: 'Все сохранённые посты',
    icon: 'i-lucide-layers-3',
  },
  ...sources.value.map(item => ({
    value: String(item.id),
    label: item.name,
    username: item.username || '',
    avatarUrl: item.avatar_url || '',
  })),
]);
const groupOptions = computed(() =>
  groups.value.map(item => ({
    value: item.id,
    label: item.ready ? item.title : `${item.title} · проверка доступа`,
  }))
);
const calendarGroupOptions = computed(() =>
  groups.value.map(item => ({ value: String(item.id), label: item.title }))
);
const calendarGroupItem = computed(() =>
  groups.value.find(item => item.id === Number(calendarGroup.value))
);
const sourceNames = computed(() =>
  Object.fromEntries(sources.value.map(item => [item.id, item.name]))
);
const sourcesById = computed(() =>
  Object.fromEntries(sources.value.map(item => [item.id, item]))
);
const calendarEvents = computed(() =>
  runs.value.map(item =>
    publicationEvent(
      item,
      sourceNames.value[item.source_inbox_id] || '',
      sourcesById.value[item.source_inbox_id]
    )
  )
);
const clientOptions = computed(() => [
  { value: '', label: 'Без клиента' },
  ...clients.value
    .filter(
      item =>
        item.source_inbox_id === Number(runForm.source) &&
        (!runForm.group || item.group_entry_id === Number(runForm.group))
    )
    .map(item => ({
      value: item.id,
      label: `${item.name} · осталось ${item.remaining_slots}`,
    })),
]);
const schedulePostOptions = computed(() =>
  posts.value.map(item => ({
    value: item.id,
    label: `${item.title} · ${sourceNames.value[item.source_inbox_id] || 'Telegram'}`,
  }))
);
const locked = computed(
  () => saving.value || uploading.value || deletingPostId.value !== null
);

const scheduleConfirmDisabled = computed(
  () =>
    locked.value ||
    !chosenPost.value ||
    !runForm.group ||
    !runForm.runAt ||
    !scheduleTimezone.value ||
    scheduleLoading.value ||
    Boolean(scheduleTarget.value && !scheduleAccountReady.value)
);

function mobileDate(value) {
  return new Date(value).toLocaleString('ru-RU', {
    timeZone: runTimezone.value,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function endpoint(path) {
  return `/api/v1/accounts/${route.params.accountId}/tg_publications${path}`;
}
function requestCancelled(requestError) {
  return (
    requestError?.code === 'ERR_CANCELED' ||
    requestError?.name === 'CanceledError'
  );
}
async function request(path, method, data, signal = requests.signal) {
  try {
    return (
      await axios({
        url: endpoint(path),
        method: method || 'get',
        data,
        timeout: 20000,
        signal,
      })
    ).data;
  } catch (e) {
    if (!requestCancelled(e)) {
      if ([401, 403].includes(e?.response?.status)) denied.value = true;
      if (e?.response?.status === 404 && path === '/sources')
        unavailable.value = true;
    }
    throw e;
  }
}
async function loadGroups(
  source = runForm.source || clientForm.source || postForm.source
) {
  groups.value = source
    ? (await request(`/groups?source_inbox_id=${source}`)).items
    : [];
}
function resetPostsRequest() {
  postsEpoch += 1;
  postsRequest?.abort();
  postsRequest = null;
  postsLoading.value = false;
}
function postCardsPath(cursor = null) {
  const query = new URLSearchParams();
  if (postSource.value) query.set('source_inbox_id', postSource.value);
  if (cursor) query.set('before', String(cursor));
  const value = query.toString();
  return value ? `/posts?${value}` : '/posts';
}
async function loadPostCards({ append = false } = {}) {
  const cursor = append ? postCardsCursor.value : null;
  if (append && !cursor) return;
  postsEpoch += 1;
  const current = postsEpoch;
  postsRequest?.abort();
  postsRequest = new AbortController();
  const controller = postsRequest;
  postsLoading.value = true;
  postsError.value = '';
  try {
    const data = await request(
      postCardsPath(cursor),
      undefined,
      undefined,
      controller.signal
    );
    if (current !== postsEpoch) return;
    postCards.value = append ? [...postCards.value, ...data.items] : data.items;
    postCardsCursor.value = data.next_cursor || null;
  } catch (e) {
    if (current === postsEpoch && !requestCancelled(e))
      postsError.value = errorText(e);
  } finally {
    if (current === postsEpoch) {
      postsLoading.value = false;
      postsRequest = null;
    }
  }
}
function updatePostQuery() {
  const query = { ...route.query };
  if (postSource.value) query.post_account = postSource.value;
  else delete query.post_account;
  if (
    String(route.query.post_account || '') !== String(query.post_account || '')
  )
    router.replace({ query });
}
function changePostSource() {
  updatePostQuery();
  loadPostCards();
}
async function load() {
  epoch += 1;
  const current = epoch;
  loading.value = true;
  error.value = '';
  denied.value = false;
  unavailable.value = false;
  try {
    const [sourceData, postData, clientData] = await Promise.all([
      request('/sources'),
      request('/posts'),
      request('/clients'),
    ]);
    if (current !== epoch) return;
    sources.value = sourceData.items;
    posts.value = postData.items;
    clients.value = clientData.items;
    const requestedPostSource = normalizePostSource(route.query.post_account);
    postSource.value = sources.value.some(
      item => String(item.id) === requestedPostSource
    )
      ? requestedPostSource
      : '';
    if (requestedPostSource !== postSource.value) updatePostQuery();
    if (postSource.value) await loadPostCards();
    else {
      resetPostsRequest();
      postCards.value = postData.items;
      postCardsCursor.value = postData.next_cursor || null;
      postsError.value = '';
    }
    if (sources.value.length) {
      await loadGroups(sources.value[0].id);
      const requested = normalizeGroupId(calendarGroup.value);
      calendarGroup.value = groups.value.some(
        item => String(item.id) === requested
      )
        ? requested
        : String(groups.value[0]?.id || '');
      // eslint-disable-next-line no-use-before-define
      updateCalendarQuery();
      if (calendarRange.value && calendarGroup.value) {
        // eslint-disable-next-line no-use-before-define
        await loadRuns();
      }
    } else {
      groups.value = [];
      runs.value = [];
    }
  } catch (e) {
    if (current === epoch) error.value = errorText(e);
  } finally {
    if (current === epoch) loading.value = false;
  }
}
function resetRunsRequest() {
  runsEpoch += 1;
  runsRequest?.abort();
  runsRequest = null;
  activeRunsKey = '';
  completedRunsKey = '';
  calendarLoading.value = false;
}
async function loadRuns(force = false) {
  if (!calendarRange.value || !calendarGroup.value) {
    resetRunsRequest();
    runs.value = [];
    return;
  }
  const key = calendarRangeSignature(calendarRange.value, calendarGroup.value);
  if (!force && (key === activeRunsKey || key === completedRunsKey)) return;
  runsEpoch += 1;
  const current = runsEpoch;
  runsRequest?.abort();
  runsRequest = new AbortController();
  const controller = runsRequest;
  activeRunsKey = key;
  calendarLoading.value = true;
  calendarError.value = '';
  try {
    const query = new URLSearchParams({
      from: calendarRange.value.start,
      to: calendarRange.value.end,
      group_entry_id: calendarGroup.value,
    });
    const data = await request(
      `/runs?${query.toString()}`,
      undefined,
      undefined,
      controller.signal
    );
    if (current !== runsEpoch || key !== activeRunsKey) return;
    runs.value = data.items;
    calendarTruncated.value = Boolean(data.truncated);
    completedRunsKey = key;
  } catch (e) {
    if (current === runsEpoch && !requestCancelled(e))
      calendarError.value = errorText(e);
  } finally {
    if (current === runsEpoch) {
      calendarLoading.value = false;
      activeRunsKey = '';
      runsRequest = null;
    }
  }
}
function updateCalendarQuery() {
  const query = { ...route.query };
  if (calendarGroup.value) query.group = calendarGroup.value;
  else delete query.group;
  query.view = calendarView.value;
  query.date = calendarDate.value;
  if (
    String(route.query.group || '') !== String(query.group || '') ||
    String(route.query.view || '') !== String(query.view || '') ||
    String(route.query.date || '') !== String(query.date || '')
  )
    router.replace({ query });
}
function handleCalendarRange(range) {
  const signature = calendarRangeSignature(range);
  calendarRange.value = range;
  calendarView.value = normalizeCalendarView(range.view);
  calendarDate.value = normalizeCalendarDate(range.date);
  updateCalendarQuery();
  if (signature !== lastCalendarRangeSignature) {
    lastCalendarRangeSignature = signature;
  }
  loadRuns();
}
function changeCalendarView(value) {
  calendarView.value = normalizeCalendarView(value);
  updateCalendarQuery();
}
function changeCalendarGroup() {
  updateCalendarQuery();
  loadRuns();
}
async function loadFillSettings() {
  fillEpoch += 1;
  const current = fillEpoch;
  fillConfigured.value = false;
  savedFillSettings.value = null;
  fillResult.value = null;
  lastFill.value = null;
  fillError.value = '';
  fillRequestKey = '';
  if (!calendarGroup.value) return;
  fillLoading.value = true;
  try {
    const result = await request(
      `/calendar/settings?group_entry_id=${calendarGroup.value}`
    );
    if (current !== fillEpoch) return;
    const settings = result.settings;
    savedFillSettings.value = settings || null;
    Object.assign(
      fillForm,
      settings
        ? {
            sources: settings.source_inbox_ids,
            ...fillFormSettings(settings),
            times: (settings.daily_times || ['11:00', '18:00']).join(', '),
            days: String(settings.days),
            weekdays: settings.weekdays,
          }
        : {
            sources: [],
            ...fillFormSettings({}),
            times: '11:00, 18:00',
            days: '7',
            weekdays: [1, 2, 3, 4, 5, 6, 7],
          }
    );
    fillConfigured.value = Boolean(settings);
    lastFill.value = result.last_fill || null;
  } catch (e) {
    if (current === fillEpoch && !requestCancelled(e))
      fillError.value = errorText(e);
  } finally {
    if (current === fillEpoch) fillLoading.value = false;
  }
}
function openFillSettings() {
  if (locked.value || fillLoading.value) return;
  fillError.value = '';
  if (savedFillSettings.value) {
    const settings = savedFillSettings.value;
    Object.assign(fillForm, {
      sources: [...settings.source_inbox_ids],
      ...fillFormSettings(settings),
      times: (settings.daily_times || ['11:00', '18:00']).join(', '),
      days: String(settings.days),
      weekdays: [...settings.weekdays],
    });
  }
  fillSettingsDialog.value?.open();
}
async function saveFillSettings() {
  if (locked.value || !fillValid.value) return;
  saving.value = true;
  fillError.value = '';
  try {
    const settings = {
      group_entry_id: Number(calendarGroup.value),
      source_inbox_ids: fillForm.sources.map(Number),
      timezone: calendarTimezone.value,
      mode: fillForm.mode,
      ...(fillForm.mode === 'random'
        ? {
            daily_min: Number(fillForm.dailyMin),
            daily_max: Number(fillForm.dailyMax),
            time_from: fillForm.timeFrom,
            time_to: fillForm.timeTo,
          }
        : { daily_times: fillTimes.value }),
      weekdays: [...fillForm.weekdays],
      days: Number(fillForm.days),
    };
    await request('/calendar/settings', 'post', {
      request_key: uuid(),
      ...settings,
    });
    savedFillSettings.value = settings;
    fillConfigured.value = true;
    fillRequestKey = '';
    fillSettingsDialog.value?.close();
    notice.value =
      'Настройки сохранены. Нажмите «Заполнить», чтобы добавить задания в календарь.';
  } catch (e) {
    fillError.value = errorText(e);
  } finally {
    saving.value = false;
  }
}
async function fillCalendar() {
  if (locked.value || fillLoading.value || !calendarGroup.value) return;
  if (!fillConfigured.value) {
    openFillSettings();
    return;
  }
  saving.value = true;
  fillError.value = '';
  fillResult.value = null;
  if (!fillRequestKey) fillRequestKey = uuid();
  const group = calendarGroup.value;
  try {
    const result = await request('/calendar/fill', 'post', {
      request_key: fillRequestKey,
      group_entry_id: Number(group),
      from_date: fillFrom.value,
      settings: {
        ...savedFillSettings.value,
        timezone: calendarTimezone.value,
      },
    });
    fillRequestKey = '';
    if (calendarGroup.value !== group) return;
    fillResult.value = result;
    if (result.created) lastFill.value = result;
    notice.value = result.created
      ? `В календарь добавлено: ${result.created}.`
      : 'Новые задания не добавлены. Существующие публикации сохранены.';
    calendarDate.value = fillFrom.value;
    updateCalendarQuery();
    await loadRuns(true);
  } catch (e) {
    fillError.value = errorText(e);
  } finally {
    saving.value = false;
  }
}
async function cancelLastFill() {
  if (locked.value || !lastFill.value) return;
  saving.value = true;
  fillError.value = '';
  try {
    const result = await request(
      `/calendar/fills/${lastFill.value.batch_id}/cancel`,
      'post',
      { request_key: uuid() }
    );
    notice.value = `Отменено: ${result.cancelled}. Начатые и завершённые публикации сохранены: ${result.retained}.`;
    lastFill.value = null;
    cancelFillDialog.value?.close();
    await loadRuns(true);
  } catch (e) {
    fillError.value = errorText(e);
  } finally {
    saving.value = false;
  }
}
function resetPost() {
  Object.assign(postForm, {
    source: sources.value[0]?.id || '',
    title: '',
    body: '',
  });
  postFiles.value = [];
  if (fileInput.value) fileInput.value.value = '';
}
async function openPost() {
  resetPost();
  await nextTick();
  postDialog.value?.open();
}
async function openPostPreview(post) {
  previewPost.value = post;
  await nextTick();
  postPreviewDialog.value?.open();
}
function closePostPreview() {
  previewPost.value = null;
}
async function openDeletePost(post) {
  if (locked.value) return;
  postToDelete.value = post;
  postDeleteError.value = '';
  deletePostRequestKey.value = uuid();
  await nextTick();
  deletePostDialog.value?.open();
}
function closeDeletePost() {
  if (deletingPostId.value !== null) return;
  postToDelete.value = null;
  postDeleteError.value = '';
  deletePostRequestKey.value = '';
}
async function deletePost() {
  if (deletingPostId.value !== null || !postToDelete.value) return;
  const post = postToDelete.value;
  deletingPostId.value = post.id;
  postDeleteError.value = '';
  try {
    await request(`/posts/${post.id}/archive`, 'post', {
      request_key: deletePostRequestKey.value,
      lock_version: post.lock_version,
    });
    posts.value = posts.value.filter(item => item.id !== post.id);
    postCards.value = postCards.value.filter(item => item.id !== post.id);
    if (chosenPost.value?.id === post.id) chosenPost.value = null;
    if (previewPost.value?.id === post.id) previewPost.value = null;
    deletePostDialog.value?.close();
    postToDelete.value = null;
    postDeleteError.value = '';
    deletePostRequestKey.value = '';
    notice.value = 'Пост удалён.';
  } catch (e) {
    postDeleteError.value = errorText(e);
  } finally {
    deletingPostId.value = null;
  }
}
function closeSchedule() {
  scheduleEpoch += 1;
  scheduleRequest?.abort();
  scheduleLoading.value = false;
  scheduleError.value = '';
}
async function openSchedule(post) {
  closeSchedule();
  scheduleTarget.value = null;
  scheduleSubmission = null;
  chosenPost.value = post;
  Object.assign(runForm, {
    post: post.id,
    source: post.source_inbox_id,
    group: calendarGroup.value || '',
    client: '',
    runAt: '',
  });
  error.value = '';
  await loadGroups(post.source_inbox_id);
  await nextTick();
  scheduleDialog.value?.open();
}
async function changeRunPost() {
  const post = posts.value.find(item => item.id === Number(runForm.post));
  chosenPost.value = post || null;
  if (!post) return;
  runForm.source = post.source_inbox_id;
  runForm.client = '';
  await loadGroups(post.source_inbox_id);
  runForm.group = groups.value.some(
    item => String(item.id) === String(calendarGroup.value)
  )
    ? calendarGroup.value
    : '';
}
async function loadSchedulePosts(append = false) {
  if (
    !runForm.source ||
    (append && (!scheduleCursor.value || scheduleLoading.value))
  )
    return;
  scheduleEpoch += 1;
  const current = scheduleEpoch;
  scheduleRequest?.abort();
  const controller = new AbortController();
  scheduleRequest = controller;
  scheduleLoading.value = true;
  scheduleError.value = '';
  const source = Number(runForm.source);
  try {
    const [postData, groupData] = await Promise.all([
      request(
        `/posts?source_inbox_id=${source}${append ? `&before=${scheduleCursor.value}` : ''}`,
        undefined,
        undefined,
        controller.signal
      ),
      append
        ? Promise.resolve(null)
        : request(
            `/groups?source_inbox_id=${source}`,
            undefined,
            undefined,
            controller.signal
          ),
    ]);
    if (current !== scheduleEpoch) return;
    schedulePosts.value = append
      ? [
          ...schedulePosts.value,
          ...postData.items.filter(
            item =>
              !schedulePosts.value.some(existing => existing.id === item.id)
          ),
        ]
      : postData.items;
    scheduleCursor.value = postData.next_cursor || null;
    if (groupData)
      scheduleAccountReady.value = groupData.items.some(
        group =>
          String(group.id) === String(scheduleTarget.value?.id) && group.ready
      );
    if (!scheduleAccountReady.value)
      scheduleError.value =
        'Аккаунт пока не готов писать в выбранную группу. Проверьте подключение и доступ.';
  } catch (e) {
    if (current === scheduleEpoch && !requestCancelled(e))
      scheduleError.value = errorText(e);
  } finally {
    if (current === scheduleEpoch) scheduleLoading.value = false;
  }
}
function changeScheduleAccount() {
  chosenPost.value = null;
  runForm.post = '';
  runForm.client = '';
  schedulePosts.value = [];
  scheduleCursor.value = null;
  scheduleAccountReady.value = false;
  scheduleSubmission = null;
  loadSchedulePosts();
}
function chooseSchedulePost() {
  chosenPost.value =
    schedulePosts.value.find(
      post =>
        post.id === Number(runForm.post) &&
        post.source_inbox_id === Number(runForm.source)
    ) || null;
}
async function openCalendarSchedule(value = null) {
  if (locked.value || !calendarGroupItem.value?.timezone) return;
  closeSchedule();
  scheduleTarget.value = { ...calendarGroupItem.value };
  schedulePosts.value = [];
  scheduleCursor.value = null;
  scheduleAccountReady.value = false;
  scheduleSubmission = null;
  const clicked = value?.dateStr;
  const date = value instanceof Date ? value : value?.date;
  let runAt = calendarDateTime(
    new Date(Date.now() + 3600000),
    scheduleTarget.value.timezone
  );
  if (clicked)
    runAt = value.allDay
      ? `${clicked.slice(0, 10)}T10:00`
      : clicked.slice(0, 16);
  else if (date instanceof Date)
    runAt = `${dateInCalendarTimezone(date, scheduleTarget.value.timezone)}T10:00`;
  chosenPost.value = null;
  Object.assign(runForm, {
    post: '',
    source: '',
    group: String(scheduleTarget.value.id),
    client: '',
    runAt,
  });
  await nextTick();
  scheduleDialog.value?.open();
}

async function openRun(run) {
  selectedRun.value = run;
  runDetail.value = null;
  runEditBody.value = '';
  runDetailError.value = '';
  runEditTime.value = calendarDateTime(run.run_at, runTimezone.value);
  replacementOpen.value = false;
  runDetailEpoch += 1;
  const current = runDetailEpoch;
  runDetailLoading.value = true;
  await nextTick();
  runDialog.value?.open();
  try {
    const detail = await request(`/runs/${run.id}`);
    if (current !== runDetailEpoch) return;
    selectedRun.value = detail.run;
    runEditTime.value = calendarDateTime(detail.run.run_at, runTimezone.value);
    runDetail.value = detail.post;
    runEditBody.value = detail.post.body;
  } catch (e) {
    if (current === runDetailEpoch) runDetailError.value = errorText(e);
  } finally {
    if (current === runDetailEpoch) runDetailLoading.value = false;
  }
}
async function saveRunChanges() {
  if (locked.value || !selectedRun.value || runDetailLoading.value) return;
  saving.value = true;
  runDetailError.value = '';
  try {
    await request(`/runs/${selectedRun.value.id}`, 'patch', {
      request_key: uuid(),
      lock_version: selectedRun.value.lock_version,
      run_at: calendarDateTimeToIso(runEditTime.value, runTimezone.value),
      timezone: runTimezone.value,
      body: runEditBody.value,
    });
    notice.value = 'Изменения публикации сохранены.';
    await openRun(selectedRun.value);
    await loadRuns(true);
  } catch (e) {
    runDetailError.value = errorText(e);
  } finally {
    saving.value = false;
  }
}
async function loadReplacementPosts(append = false) {
  if (!selectedRun.value || replacementLoading.value) return;
  replacementOpen.value = true;
  replacementLoading.value = true;
  runDetailError.value = '';
  const id = selectedRun.value.id;
  try {
    const data = await request(
      `/posts?source_inbox_id=${selectedRun.value.source_inbox_id}${append && replacementCursor.value ? `&before=${replacementCursor.value}` : ''}`
    );
    if (selectedRun.value?.id !== id) return;
    replacementPosts.value = append
      ? [...replacementPosts.value, ...data.items]
      : data.items;
    replacementCursor.value = data.next_cursor || null;
    if (!append) runReplacement.value = '';
  } catch (e) {
    runDetailError.value = errorText(e);
  } finally {
    replacementLoading.value = false;
  }
}
async function replaceRunPost() {
  if (locked.value || !selectedRun.value || !runReplacement.value) return;
  saving.value = true;
  runDetailError.value = '';
  try {
    await request(`/runs/${selectedRun.value.id}/post`, 'post', {
      request_key: uuid(),
      lock_version: selectedRun.value.lock_version,
      post_id: Number(runReplacement.value),
    });
    await openRun(selectedRun.value);
    await loadRuns(true);
  } catch (e) {
    runDetailError.value = errorText(e);
  } finally {
    saving.value = false;
  }
}
async function openClient() {
  Object.assign(clientForm, {
    source: sources.value[0]?.id || '',
    group: '',
    name: '',
    username: '',
    slots: '1',
    notes: '',
  });
  await loadGroups(clientForm.source);
  await nextTick();
  clientDialog.value?.open();
}
function chooseFiles(event) {
  const files = [...event.target.files];
  const total = files.reduce((sum, file) => sum + file.size, 0);
  if (files.length > 10 || total > 50 * 1024 * 1024) {
    error.value =
      files.length > 10
        ? 'Можно добавить не более 10 файлов.'
        : 'Общий размер файлов не должен превышать 50 МБ.';
    event.target.value = '';
    postFiles.value = [];
    return;
  }
  postFiles.value = files;
}
function upload(file) {
  return new Promise((resolve, reject) => {
    new DirectUpload(file, '/rails/active_storage/direct_uploads').create(
      (uploadError, blob) =>
        uploadError ? reject(uploadError) : resolve(blob.signed_id)
    );
  });
}
async function savePost() {
  if (locked.value) return;
  saving.value = true;
  uploading.value = postFiles.value.length > 0;
  error.value = '';
  try {
    const media = await Promise.all(postFiles.value.map(file => upload(file)));
    await request('/posts', 'post', {
      request_key: uuid(),
      source_inbox_id: Number(postForm.source),
      title: postForm.title.trim(),
      body: postForm.body.trim(),
      media_blob_ids: media,
    });
    postDialog.value?.close();
    notice.value = 'Пост сохранён.';
    await load();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    saving.value = false;
    uploading.value = false;
  }
}
async function saveRun() {
  if (
    locked.value ||
    !chosenPost.value ||
    !scheduleTimezone.value ||
    scheduleLoading.value
  )
    return;
  if (
    scheduleTarget.value &&
    (!scheduleAccountReady.value ||
      chosenPost.value.source_inbox_id !== Number(runForm.source))
  )
    return;
  saving.value = true;
  scheduleError.value = '';
  try {
    const payload = {
      post_id: chosenPost.value.id,
      group_entry_id: Number(scheduleTarget.value?.id || runForm.group),
      client_id: runForm.client ? Number(runForm.client) : null,
      run_at: calendarDateTimeToIso(runForm.runAt, scheduleTimezone.value),
      timezone: scheduleTimezone.value,
    };
    const fingerprint = JSON.stringify(payload);
    if (!scheduleSubmission || scheduleSubmission.fingerprint !== fingerprint)
      scheduleSubmission = {
        fingerprint,
        data: { ...payload, request_key: uuid(), idempotency_key: uuid() },
      };
    await request('/runs', 'post', scheduleSubmission.data);
    scheduleDialog.value?.close();
    notice.value = 'Публикация добавлена в календарь.';
    await loadRuns(true);
  } catch (e) {
    scheduleError.value = errorText(e);
  } finally {
    saving.value = false;
  }
}

async function saveClient() {
  if (locked.value) return;
  saving.value = true;
  error.value = '';
  try {
    await request('/clients', 'post', {
      request_key: uuid(),
      source_inbox_id: Number(clientForm.source),
      group_entry_id: Number(clientForm.group),
      name: clientForm.name.trim(),
      username: clientForm.username.trim(),
      sold_slots: Number(clientForm.slots),
      notes: clientForm.notes.trim(),
    });
    clientDialog.value?.close();
    notice.value = 'Клиент добавлен.';
    await load();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    saving.value = false;
  }
}
function askDeleteRun(run) {
  if (locked.value || run.state !== 'scheduled') return;
  runToDelete.value = run;
  runDeleteError.value = '';
  deleteRunRequestKey.value = uuid();
  deleteRunDialog.value?.open();
}
async function cancelRun() {
  const run = runToDelete.value;
  if (!run) return;
  if (locked.value || run.state !== 'scheduled') return;
  saving.value = true;
  try {
    await request(`/runs/${run.id}/cancel`, 'post', {
      request_key: deleteRunRequestKey.value,
      lock_version: run.lock_version,
    });
    notice.value = 'Публикация удалена из расписания. История сохранена.';
    deleteRunDialog.value?.close();
    if (selectedRun.value?.id === run.id) runDialog.value?.close();
    await loadRuns(true);
  } catch (e) {
    runDeleteError.value = errorText(e);
  } finally {
    saving.value = false;
  }
}
watch(
  () => route.params.accountId,
  () => {
    requests.abort();
    closeSchedule();
    scheduleDialog.value?.close();
    requests = new AbortController();
    fillConfigured.value = false;
    savedFillSettings.value = null;
    lastFill.value = null;
    fillEpoch += 1;
    fillSettingsDialog.value?.close();
    resetPostsRequest();
    resetRunsRequest();
    load();
  },
  { immediate: true }
);
watch(
  () => route.query.post_account,
  value => {
    const requested = normalizePostSource(value);
    const next = sources.value.some(item => String(item.id) === requested)
      ? requested
      : '';
    if (next === postSource.value) return;
    postSource.value = next;
    loadPostCards();
  }
);
watch(
  () => [route.query.group, route.query.view, route.query.date],
  ([group, view, date]) => {
    const nextGroup = normalizeGroupId(group);
    const nextView = normalizeCalendarView(view);
    const nextDate = normalizeCalendarDate(
      date,
      new Date(),
      calendarTimezone.value || CALENDAR_TIMEZONE
    );
    const groupChanged = nextGroup && nextGroup !== calendarGroup.value;
    if (
      groupChanged &&
      groups.value.some(item => String(item.id) === nextGroup)
    )
      calendarGroup.value = nextGroup;
    calendarView.value = nextView;
    calendarDate.value = nextDate;
    if (groupChanged && calendarGroup.value === nextGroup) loadRuns();
  }
);
watch(
  calendarGroup,
  () => {
    loadFillSettings();
  },
  { immediate: true }
);
watch(calendarTimezone, zone => {
  resetRunsRequest();
  calendarRange.value = null;
  runs.value = [];
  if (!zone) return;
  const today = dateInCalendarTimezone(new Date(), zone);
  if (!firstTimezoneResolved && !initialCalendarDateProvided)
    calendarDate.value = today;
  firstTimezoneResolved = true;
  fillFrom.value = calendarDate.value < today ? today : calendarDate.value;
});
watch(fillFrom, () => {
  fillRequestKey = '';
});
watch(calendarDate, value => {
  if (!locked.value)
    fillFrom.value = value < calendarToday.value ? calendarToday.value : value;
});
onBeforeUnmount(() => {
  closeSchedule();
  epoch += 1;
  fillEpoch += 1;
  requests.abort();
  resetPostsRequest();
  resetRunsRequest();
});
</script>

<template>
  <SettingsLayout class="min-w-0">
    <template #header>
      <BaseSettingsHeader
        title="Публикации"
        description="Планирование постов в Telegram-группах через ваши подключённые аккаунты."
      />
      <div class="max-w-full overflow-x-auto py-1">
        <TabBar
          :tabs="tabs"
          :initial-active-tab="tabs.findIndex(item => item.key === tab)"
          class="min-w-max"
          @tab-changed="value => (tab = value.key)"
        />
      </div>
    </template>
    <template #body>
      <Banner v-if="denied" color="ruby" role="alert">
        Нет доступа. Раздел доступен администратору рабочей области.
      </Banner>
      <Banner v-else-if="unavailable" color="amber" role="status">
        Модуль публикаций пока не включён.
      </Banner>
      <template v-else>
        <Banner v-if="error" color="ruby" role="alert" class="mb-4">
          {{ error }}
          <Button type="button" label="Повторить" slate ghost @click="load" />
        </Banner>
        <Banner v-if="notice" color="teal" role="status" class="mb-4">
          {{ notice }}
        </Banner>
        <p v-if="loading" role="status" class="text-sm text-n-slate-11">
          Загрузка публикаций…
        </p>

        <section
          v-else-if="!error && tab === 'calendar'"
          aria-label="Календарь публикаций"
          data-range-guard="tg_publication_calendar_range_guard"
          data-request-guard="tg_publication_calendar_request_guard"
          class="min-w-0"
        >
          <div class="mb-5 flex flex-wrap items-end justify-between gap-3">
            <div class="min-w-64 flex-1 sm:max-w-md">
              <label class="mb-2 block text-sm font-medium text-n-slate-12">
                Telegram-группа
              </label>
              <ComboBox
                v-model="calendarGroup"
                :options="calendarGroupOptions"
                :disabled="locked"
                placeholder="Выберите группу"
                aria-label="Telegram-группа календаря"
                @update:model-value="changeCalendarGroup"
              />
            </div>
            <div class="flex items-center gap-3">
              <span class="text-xs text-n-slate-10"
                >Местное время: {{ calendarTimeLabel }}</span
              >
              <Button
                type="button"
                label="Обновить"
                icon="i-lucide-refresh-cw"
                slate
                faded
                :disabled="calendarLoading || !calendarGroup"
                @click="loadRuns"
              />
            </div>
          </div>
          <div
            class="mb-4 flex flex-wrap items-end gap-3"
            data-feature="tg_publication_calendar_fill"
          >
            <div class="w-44">
              <Input
                v-model="fillFrom"
                type="date"
                label="Начать заполнение с"
                :min="calendarToday.value"
                :disabled="locked || fillLoading"
              />
            </div>
            <Button
              type="button"
              :label="fillButtonLabel"
              icon="i-lucide-calendar-check"
              :disabled="
                locked || fillLoading || !calendarGroup || !calendarTimezone
              "
              @click="fillCalendar"
            />
            <Button
              type="button"
              label="Настройки заполнения"
              icon="i-lucide-settings-2"
              slate
              faded
              :disabled="
                locked || fillLoading || !calendarGroup || !calendarTimezone
              "
              @click="openFillSettings"
            />
            <Button
              v-if="lastFill"
              type="button"
              label="Отменить последнее заполнение"
              icon="i-lucide-undo-2"
              slate
              faded
              :disabled="locked"
              @click="cancelFillDialog.open()"
            />
          </div>
          <p class="mb-4 text-sm text-n-slate-11">
            Заполнение создаёт задания сразу в календаре. Они отправятся в
            назначенное местное время группы. Посты и аккаунты выбираются
            случайно. Один пост — максимум один раз за местные сутки в группе.
          </p>
          <p v-if="fillLoading" role="status" class="text-sm text-n-slate-11">
            Загружаем настройки заполнения…
          </p>
          <Banner v-if="fillError" color="ruby" role="alert" class="mb-4">
            {{ fillError }}
            <Button
              type="button"
              label="Обновить настройки"
              slate
              ghost
              :disabled="locked"
              @click="loadFillSettings"
            />
          </Banner>
          <div
            v-if="fillResult"
            role="status"
            class="mb-4 text-sm text-n-slate-11"
          >
            <p v-if="fillResult.skipped?.unfilled">
              Не удалось заполнить публикаций:
              {{ fillResult.skipped.unfilled }}. Не хватило свободного времени
              или подходящих постов.
            </p>
            <p v-if="fillResult.unavailable_accounts?.length">
              Аккаунтов без подтверждённого доступа:
              {{ fillResult.unavailable_accounts.length }}. Проверьте
              подключение и права.
            </p>
            <p v-if="fillResult.skipped?.past">
              Прошедших часов пропущено: {{ fillResult.skipped.past }}.
            </p>
            <p v-if="fillResult.skipped?.occupied">
              Занятых часов пропущено: {{ fillResult.skipped.occupied }}.
            </p>
            <p v-if="fillResult.skipped?.repeat_limit">
              Мест без постов, подходящих по интервалу повторения:
              {{ fillResult.skipped.repeat_limit }}.
            </p>
            <p v-if="fillResult.skipped?.daily_repeat_limit">
              Не хватило уникальных постов на день. Незаполненных мест:
              {{ fillResult.skipped.daily_repeat_limit }}. Повторы в течение
              местных суток не добавлены. Добавьте посты или уменьшите
              количество публикаций в день.
            </p>
            <p v-if="fillResult.skipped?.no_available_posts">
              Мест без доступных постов:
              {{ fillResult.skipped.no_available_posts }}.
            </p>
          </div>
          <Banner v-if="calendarError" color="ruby" role="alert" class="mb-4">
            {{ calendarError }}
            <Button
              type="button"
              label="Повторить"
              slate
              ghost
              @click="loadRuns"
            />
          </Banner>
          <Banner
            v-if="calendarTruncated"
            color="amber"
            role="status"
            class="mb-4"
          >
            В выбранном периоде больше 500 публикаций. Уточните период до недели
            или дня.
          </Banner>
          <Banner
            v-if="calendarGroup && !calendarTimezone"
            color="ruby"
            role="alert"
          >
            Город и часовой пояс группы не определены. Заполнение недоступно,
            чтобы не запланировать посты в неверное время.
          </Banner>
          <PublicationCalendar
            v-if="calendarGroup && calendarTimezone"
            :key="`${calendarGroup}:${calendarTimezone}`"
            :events="calendarEvents"
            :view="calendarView"
            :date="calendarDate"
            :loading="calendarLoading"
            :disabled="locked"
            :timezone="calendarTimezone"
            @range-change="handleCalendarRange"
            @view-change="changeCalendarView"
            @event-click="openRun"
            @delete-run="askDeleteRun"
            @slot-click="openCalendarSchedule"
            @create="openCalendarSchedule"
          />
          <p
            v-if="!calendarGroup"
            class="rounded-xl border border-dashed border-n-weak p-8 text-center text-n-slate-11"
          >
            Добавьте Telegram-группу, чтобы открыть её календарь публикаций.
          </p>
          <p
            v-else-if="
              calendarTimezone &&
              !calendarLoading &&
              !calendarError &&
              !runs.length
            "
            class="mt-4 rounded-xl border border-dashed border-n-weak p-6 text-center text-n-slate-11"
          >
            У группы «{{ calendarGroupItem?.title }}» в этом периоде публикаций
            нет. Нажмите свободную дату или «Запланировать».
          </p>
        </section>

        <section
          v-else-if="!error && tab === 'posts'"
          aria-label="Сохранённые посты"
        >
          <div class="flex flex-wrap justify-between gap-3 mb-5">
            <div>
              <h2 class="text-lg font-medium m-0">Сохранённые посты</h2>
              <p class="text-sm text-n-slate-11 m-0">
                При планировании сохраняется отдельная неизменяемая копия.
              </p>
            </div>
            <Button
              type="button"
              label="Новый пост"
              icon="i-lucide-plus"
              :disabled="!sources.length"
              @click="openPost"
            />
          </div>
          <div class="mb-5 min-w-0 sm:max-w-md">
            <label class="mb-2 block text-sm font-medium text-n-slate-12">
              Telegram-аккаунт
            </label>
            <TelegramAccountComboBox
              v-model="postSource"
              :options="postSourceOptions"
              placeholder="Все аккаунты"
              aria-label="Фильтр постов по Telegram-аккаунту"
              @update:model-value="changePostSource"
            />
          </div>
          <Banner v-if="postsError" color="ruby" role="alert" class="mb-4">
            {{ postsError }}
            <Button
              type="button"
              label="Повторить"
              slate
              ghost
              @click="loadPostCards()"
            />
          </Banner>
          <p
            v-if="postsLoading && !postCards.length"
            role="status"
            class="text-sm text-n-slate-11"
          >
            Загрузка постов…
          </p>
          <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <article
              v-for="post in postCards"
              :key="post.id"
              class="flex min-w-0 flex-col rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div class="flex min-w-0 items-start justify-between gap-3">
                <div class="flex min-w-0 items-center gap-3">
                  <Avatar
                    :src="sourcesById[post.source_inbox_id]?.avatar_url || ''"
                    :name="
                      sourcesById[post.source_inbox_id]?.name || 'Telegram'
                    "
                    :size="36"
                    rounded-full
                  />
                  <div class="min-w-0">
                    <p class="m-0 truncate text-sm font-medium text-n-slate-12">
                      {{
                        sourcesById[post.source_inbox_id]?.name ||
                        'Telegram-аккаунт'
                      }}
                    </p>
                    <p class="m-0 truncate text-xs text-n-slate-10">
                      {{
                        sourcesById[post.source_inbox_id]?.username
                          ? `@${sourcesById[post.source_inbox_id].username}`
                          : 'Username не указан'
                      }}
                    </p>
                  </div>
                </div>
                <div class="flex shrink-0 items-center gap-2">
                  <template v-if="post.media.length">
                    <span class="text-xs text-n-slate-10">
                      Файлов: {{ post.media.length }}
                    </span>
                  </template>
                  <Button
                    type="button"
                    icon="i-lucide-trash-2"
                    ruby
                    ghost
                    sm
                    :aria-label="`Удалить пост «${post.title}»`"
                    :title="`Удалить пост «${post.title}»`"
                    :disabled="locked"
                    @click="openDeletePost(post)"
                  />
                </div>
              </div>
              <h3 class="mt-4 mb-0 break-words font-medium text-n-slate-12">
                {{ post.title }}
              </h3>
              <PostMediaGrid :media="post.media" />
              <p
                class="mt-3 mb-4 flex-1 whitespace-pre-wrap break-words text-sm line-clamp-4"
              >
                {{ post.body }}
              </p>
              <div class="flex flex-wrap gap-2">
                <Button
                  type="button"
                  label="Посмотреть пост"
                  icon="i-lucide-eye"
                  slate
                  faded
                  @click="openPostPreview(post)"
                />
                <Button
                  type="button"
                  label="Запланировать"
                  icon="i-lucide-calendar-plus"
                  :disabled="locked"
                  @click="openSchedule(post)"
                />
              </div>
            </article>
          </div>
          <p
            v-if="!postsLoading && !postsError && !postCards.length"
            class="rounded-xl border border-dashed border-n-weak p-8 text-center text-n-slate-11"
          >
            {{
              postSource
                ? 'У выбранного аккаунта сохранённых постов пока нет.'
                : 'Сохранённых постов пока нет.'
            }}
          </p>
          <div v-if="postCardsCursor" class="mt-5 flex justify-center">
            <Button
              type="button"
              label="Показать ещё"
              slate
              faded
              :is-loading="postsLoading"
              :disabled="postsLoading"
              @click="loadPostCards({ append: true })"
            />
          </div>
        </section>

        <section
          v-else-if="!error && tab === 'clients'"
          aria-label="Клиенты публикаций"
        >
          <div class="flex flex-wrap justify-between gap-3 mb-5">
            <div>
              <h2 class="text-lg font-medium m-0">Клиенты</h2>
              <p class="text-sm text-n-slate-11 m-0">
                Оплаченный лимит резервируется при постановке публикации.
              </p>
            </div>
            <Button
              type="button"
              label="Добавить клиента"
              icon="i-lucide-plus"
              :disabled="!sources.length"
              @click="openClient"
            />
          </div>
          <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <article
              v-for="client in clients"
              :key="client.id"
              class="rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <h3 class="font-medium m-0 break-words">{{ client.name }}</h3>
              <p v-if="client.username" class="text-sm text-n-slate-11 mt-1">
                @{{ client.username }}
              </p>
              <div class="grid grid-cols-3 gap-2 mt-4 text-center">
                <div>
                  <strong class="block">{{ client.sold_slots }}</strong
                  ><span class="text-xs text-n-slate-10">Куплено</span>
                </div>
                <div>
                  <strong class="block">{{ client.used_slots }}</strong
                  ><span class="text-xs text-n-slate-10">Использовано</span>
                </div>
                <div>
                  <strong class="block">{{ client.remaining_slots }}</strong
                  ><span class="text-xs text-n-slate-10">Осталось</span>
                </div>
              </div>
            </article>
          </div>
          <p
            v-if="!clients.length"
            class="rounded-xl border border-dashed border-n-weak p-8 text-center text-n-slate-11"
          >
            Клиентов пока нет. Публикации без клиента тоже можно планировать.
          </p>
        </section>
      </template>

      <Dialog
        ref="fillSettingsDialog"
        overflow-y-auto
        title="Заполнение календаря"
        description="Настройки сохраняются для вас и выбранной группы. Сохранение настроек не создаёт публикации."
        confirm-button-label="Сохранить"
        cancel-button-label="Отмена"
        :disable-confirm-button="locked || !fillValid"
        @confirm="saveFillSettings"
      >
        <div class="flex flex-col gap-4">
          <p class="m-0 text-sm text-n-slate-11">
            Местное время: {{ calendarTimeLabel }}. Существующие задания не
            сдвигаются.
          </p>
          <Banner v-if="fillError" color="ruby" role="alert">
            {{ fillError }}
          </Banner>
          <fieldset :disabled="locked" class="min-w-0">
            <legend class="mb-2 text-sm font-medium">
              Аккаунты для публикаций
            </legend>
            <p v-if="!sources.length" class="text-sm text-n-slate-11">
              Подключённых аккаунтов нет.
            </p>
            <div class="mb-3 flex flex-wrap gap-2">
              <Button
                type="button"
                label="Выбрать все"
                slate
                ghost
                :disabled="locked || !sources.length || sources.length > 100"
                @click="fillForm.sources = sources.map(source => source.id)"
              />
              <Button
                type="button"
                label="Снять выбор"
                slate
                ghost
                :disabled="locked"
                @click="fillForm.sources = []"
              />
            </div>
            <div class="max-h-64 overflow-y-auto space-y-2">
              <label
                v-for="source in sources"
                :key="source.id"
                class="flex min-w-0 items-center gap-3 rounded-lg border border-n-weak p-3 cursor-pointer"
              >
                <input
                  v-model="fillForm.sources"
                  type="checkbox"
                  :value="source.id"
                  :aria-label="source.name"
                  class="shrink-0 focus-visible:outline focus-visible:outline-2"
                />
                <Avatar
                  :src="source.avatar_url || ''"
                  :name="source.name"
                  :size="32"
                />
                <span class="min-w-0 break-words text-sm">
                  {{ source.name }} ·
                  {{ source.username ? `@${source.username}` : 'Без username' }}
                </span>
              </label>
            </div>
          </fieldset>
          <Input
            v-model="fillForm.days"
            type="number"
            label="Дней с выбранной даты (1–31)"
            min="1"
            max="31"
            :disabled="locked"
          />
          <fieldset :disabled="locked" class="flex flex-wrap gap-3">
            <legend class="mb-2 text-sm font-medium">Режим заполнения</legend>
            <label class="flex items-center gap-2 text-sm">
              <input v-model="fillForm.mode" type="radio" value="fixed" />
              Фиксированные часы
            </label>
            <label class="flex items-center gap-2 text-sm">
              <input v-model="fillForm.mode" type="radio" value="random" />
              Случайное количество и время
            </label>
          </fieldset>
          <template v-if="fillForm.mode === 'random'">
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                v-model="fillForm.dailyMin"
                type="number"
                label="Публикаций в сутки: от"
                min="1"
                max="12"
                :disabled="locked"
              />
              <Input
                v-model="fillForm.dailyMax"
                type="number"
                label="Публикаций в сутки: до"
                min="1"
                max="12"
                :disabled="locked"
              />
              <Input
                v-model="fillForm.timeFrom"
                type="time"
                :label="`Время с (${calendarTimeLabel})`"
                min="10:00"
                max="21:30"
                :disabled="locked"
              />
              <Input
                v-model="fillForm.timeTo"
                type="time"
                :label="`Время до (${calendarTimeLabel})`"
                min="10:00"
                max="21:30"
                :disabled="locked"
              />
            </div>
            <p class="m-0 text-sm text-n-slate-11">
              Количество — на всю группу за день. Время выбирается случайно, с
              интервалом минимум час. Уже созданные задания учитываются и не
              меняются. Если свободных часов или подходящих постов мало,
              публикаций будет меньше.
            </p>
            <p
              v-if="!randomFillValid"
              role="alert"
              class="m-0 text-sm text-n-ruby-11"
            >
              Укажите от 1 до 12 публикаций и окно от 10:00 до 21:30, вмещающее
              максимум публикаций с интервалом час.
            </p>
          </template>
          <template v-else>
            <Input
              v-model="fillForm.times"
              :label="`Время публикаций через запятую (${calendarTimeLabel})`"
              placeholder="11:00, 18:00"
              :disabled="locked"
            />
            <p class="m-0 text-sm text-n-slate-11">
              От 10:00 до 21:30, с интервалом минимум час. Количество часов
              задаёт число публикаций в день на группу.
            </p>
          </template>
          <fieldset :disabled="locked">
            <legend class="mb-2 text-sm font-medium">Дни недели</legend>
            <div class="flex flex-wrap gap-3">
              <label
                v-for="(day, index) in [
                  'Пн',
                  'Вт',
                  'Ср',
                  'Чт',
                  'Пт',
                  'Сб',
                  'Вс',
                ]"
                :key="day"
                class="flex items-center gap-1 text-sm"
              >
                <input
                  v-model="fillForm.weekdays"
                  type="checkbox"
                  :value="index + 1"
                />{{ day }}
              </label>
            </div>
          </fieldset>
        </div>
      </Dialog>
      <Dialog
        ref="cancelFillDialog"
        title="Отменить последнее заполнение?"
        description="Отменятся только ещё не начавшиеся задания этого заполнения. Ручные задания, другие планы и история сохранятся."
        confirm-button-label="Отменить заполнение"
        cancel-button-label="Закрыть"
        :disable-confirm-button="locked"
        @confirm="cancelLastFill"
      />

      <Dialog
        ref="postDialog"
        title="Новый пост"
        description="Текст и до 10 вложений общим размером до 50 МБ."
        :disable-confirm-button="
          locked || !postForm.source || !postForm.body.trim()
        "
        :confirm-button-label="
          uploading ? 'Загружаем файлы…' : 'Сохранить пост'
        "
        @confirm="savePost"
      >
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">Telegram-аккаунт</label>
            <TelegramAccountComboBox
              v-model="postForm.source"
              :options="sourceOptions"
              placeholder="Выберите аккаунт"
              aria-label="Telegram-аккаунт"
            />
          </div>
          <Input
            v-model="postForm.title"
            label="Название"
            placeholder="Для внутреннего списка"
            :maxlength="120"
          /><TextArea
            v-model="postForm.body"
            label="Текст публикации"
            :max-length="postFiles.length ? 1024 : 4096"
            resize
          /><label class="text-sm font-medium"
            >Вложения<input
              ref="fileInput"
              class="block mt-2 w-full text-sm"
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,application/pdf"
              :disabled="locked"
              @change="chooseFiles"
          /></label>
          <p v-if="postFiles.length" class="text-sm text-n-slate-11 m-0">
            Выбрано: {{ postFiles.length }}
          </p>
        </div>
      </Dialog>
      <Dialog
        ref="postPreviewDialog"
        :title="previewPost?.title || 'Просмотр поста'"
        width="2xl"
        overflow-y-auto
        :show-confirm-button="false"
        cancel-button-label="Закрыть"
        @close="closePostPreview"
      >
        <template #description>
          <p
            autofocus
            tabindex="-1"
            class="mb-0 text-sm text-n-slate-11 outline-none"
          >
            Предпросмотр текущего формата отправки в Telegram.
          </p>
        </template>
        <TelegramPostPreview
          :post="previewPost"
          :source="sourcesById[previewPost?.source_inbox_id] || null"
        />
      </Dialog>
      <Dialog
        ref="deletePostDialog"
        data-test="delete-post-dialog"
        type="alert"
        title="Удалить пост?"
        :description="postToDelete?.title || ''"
        confirm-button-label="Удалить"
        cancel-button-label="Отмена"
        :is-loading="deletingPostId === postToDelete?.id"
        :disable-confirm-button="!postToDelete || deletingPostId !== null"
        @confirm="deletePost"
        @close="closeDeletePost"
      >
        <div class="flex flex-col gap-3">
          <Banner v-if="postDeleteError" color="ruby" role="alert">
            {{ postDeleteError }}
          </Banner>
          <p class="m-0 text-sm text-n-slate-11">
            Пост исчезнет из библиотеки. Уже запланированные публикации и
            история сохранятся.
          </p>
        </div>
      </Dialog>
      <Dialog
        ref="scheduleDialog"
        title="Запланировать публикацию"
        cancel-button-label="Отмена"
        :description="scheduleTarget?.title || chosenPost?.title || ''"
        overflow-y-auto
        @close="closeSchedule"
        :disable-confirm-button="scheduleConfirmDisabled"
        confirm-button-label="Добавить в календарь"
        @confirm="saveRun"
      >
        <div class="flex flex-col gap-4">
          <Banner v-if="scheduleError" color="ruby" role="alert">{{
            scheduleError
          }}</Banner>
          <template v-if="scheduleTarget">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium">Аккаунт</label>
              <TelegramAccountComboBox
                v-model="runForm.source"
                :options="sourceOptions"
                aria-label="Аккаунт для публикации"
                :disabled="locked"
                @update:model-value="changeScheduleAccount"
              />
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium">Пост аккаунта</label>
              <TelegramAccountComboBox
                v-model="runForm.post"
                :options="scheduleCompactOptions"
                compact
                placeholder="Выберите пост аккаунта"
                search-placeholder="Поиск по тексту поста"
                empty-state="У аккаунта нет подходящих постов"
                aria-label="Пост аккаунта"
                :disabled="
                  locked ||
                  !runForm.source ||
                  (scheduleLoading && !schedulePosts.length)
                "
                :has-more="Boolean(scheduleCursor)"
                :loading-more="scheduleLoading"
                @update:model-value="chooseSchedulePost"
                @load-more="loadSchedulePosts(true)"
              >
                <template #leading="{ option }">
                  <AuthenticatedMediaImage
                    v-if="option.thumbnail"
                    :src="option.thumbnail"
                    alt="Миниатюра поста"
                    loading="lazy"
                    class="size-7 shrink-0 overflow-hidden rounded"
                    image-class="size-7 object-cover"
                    placeholder-class="size-7 overflow-hidden text-n-slate-11"
                  />
                  <span
                    v-else
                    :class="option.icon"
                    class="size-5 shrink-0 text-n-slate-11"
                    aria-hidden="true"
                  />
                </template>
              </TelegramAccountComboBox>
              <p
                v-if="scheduleLoading"
                class="m-0 text-sm text-n-slate-11"
                role="status"
              >
                Загружаем посты…
              </p>
              <p
                v-else-if="
                  runForm.source && !schedulePosts.length && !scheduleError
                "
                class="m-0 text-sm text-n-slate-11"
              >
                У аккаунта пока нет сохранённых постов.
              </p>
              <Button
                v-if="scheduleError && runForm.source"
                label="Повторить загрузку"
                slate
                faded
                :disabled="locked || scheduleLoading"
                @click="loadSchedulePosts()"
              />
            </div>
          </template>
          <template v-else>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium">Пост</label>
              <ComboBox
                v-model="runForm.post"
                :options="schedulePostOptions"
                placeholder="Выберите сохранённый пост"
                aria-label="Пост для публикации"
                @update:model-value="changeRunPost"
              />
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium">Группа</label>
              <ComboBox
                v-model="runForm.group"
                :options="groupOptions"
                placeholder="Выберите группу"
                aria-label="Группа"
              />
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium">Клиент</label>
              <ComboBox
                v-model="runForm.client"
                :options="clientOptions"
                placeholder="Без клиента"
                aria-label="Клиент"
              />
            </div>
          </template>
          <Input
            v-model="runForm.runAt"
            type="datetime-local"
            :label="`Дата и время (${scheduleTimeLabel})`"
          />
          <p class="text-sm text-n-slate-11 m-0">
            Перед отправкой система ещё раз проверит аккаунт, прокси и право
            писать в группу.
          </p>
        </div>
        <template #footer>
          <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              label="Отмена"
              slate
              faded
              class="w-full sm:w-auto"
              :disabled="saving"
              @click="scheduleDialog.close()"
            />
            <Button
              type="button"
              label="Добавить в календарь"
              class="w-full sm:w-auto"
              :disabled="scheduleConfirmDisabled"
              :is-loading="saving"
              @click="saveRun"
            />
          </div>
        </template>
      </Dialog>
      <Dialog
        ref="runDialog"
        title="Публикация"
        overflow-y-auto
        :description="selectedRun?.group_title || ''"
        :show-cancel-button="false"
        :show-confirm-button="false"
      >
        <dl
          v-if="selectedRun"
          class="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-3 text-sm"
        >
          <dt class="text-n-slate-10">Дата и время</dt>
          <dd class="m-0 break-words">{{ mobileDate(selectedRun.run_at) }}</dd>
          <dt class="text-n-slate-10">Статус</dt>
          <dd class="m-0">
            {{ stateLabels[selectedRun.state] || 'Неизвестно' }}
          </dd>
          <dt class="text-n-slate-10">Telegram-аккаунт</dt>
          <dd class="m-0 break-words">
            {{ sourceNames[selectedRun.source_inbox_id] || 'Не указан' }}
          </dd>
          <dt class="text-n-slate-10">Вложения</dt>
          <dd class="m-0">{{ selectedRun.media_count || 0 }}</dd>
          <dt v-if="!runDetail" class="text-n-slate-10">Текст</dt>
          <dd v-if="!runDetail" class="m-0 whitespace-pre-wrap break-words">
            {{ selectedRun.text_preview || 'Без текста' }}
          </dd>
        </dl>
        <p
          v-if="runDetailLoading"
          role="status"
          class="text-sm text-n-slate-11"
        >
          Загружаем сохранённую публикацию…
        </p>
        <Banner v-if="runDetailError" color="ruby" role="alert">
          {{ runDetailError }}
        </Banner>
        <TelegramPostPreview
          v-if="runDetail"
          :post="{
            ...runDetail,
            body:
              selectedRun?.state === 'scheduled' ? runEditBody : runDetail.body,
          }"
          :source="sourcesById[selectedRun?.source_inbox_id]"
          class="mt-4"
        />
        <div
          v-if="selectedRun?.state === 'scheduled' && !runDetailLoading"
          class="mt-4 flex flex-col gap-3"
        >
          <TextArea
            v-if="runDetail"
            id="calendar-run-text"
            v-model="runEditBody"
            label="Текст этой публикации"
            :max-length="runDetail.media?.length ? 1024 : 4096"
            :disabled="locked"
            resize
            show-character-count
          />
          <p class="m-0 text-sm text-n-slate-11">
            Изменится только эта публикация. Исходный пост и другие задания
            сохранят свой текст.
          </p>
          <Input
            v-model="runEditTime"
            type="datetime-local"
            :label="`Перенести на (${runTimeLabel})`"
            :disabled="locked"
          />
          <div class="flex flex-wrap gap-2">
            <Button
              type="button"
              label="Сохранить изменения"
              slate
              faded
              :disabled="
                locked || !runEditTime || !runDetail || !runEditBody.trim()
              "
              @click="saveRunChanges"
            />
            <Button
              type="button"
              label="Заменить пост"
              slate
              faded
              :disabled="locked"
              @click="loadReplacementPosts(false)"
            />
          </div>
          <template v-if="replacementOpen">
            <ComboBox
              v-model="runReplacement"
              :options="
                replacementPosts.map(post => ({
                  value: post.id,
                  label: post.title,
                }))
              "
              placeholder="Другой пост этого аккаунта"
              aria-label="Замена поста в календаре"
            />
            <p
              v-if="replacementLoading"
              role="status"
              class="text-sm text-n-slate-11"
            >
              Загружаем посты…
            </p>
            <p
              v-else-if="!replacementPosts.length"
              class="text-sm text-n-slate-11"
            >
              У аккаунта нет доступных сохранённых постов.
            </p>
            <div class="flex flex-wrap gap-2">
              <Button
                v-if="replacementCursor"
                type="button"
                label="Ещё посты"
                slate
                faded
                :disabled="replacementLoading || locked"
                @click="loadReplacementPosts(true)"
              />
              <Button
                type="button"
                label="Сохранить замену"
                :disabled="locked || !runReplacement"
                @click="replaceRunPost"
              />
            </div>
          </template>
        </div>
        <template #footer>
          <div class="flex justify-end gap-2 border-t border-n-weak pt-4">
            <Button
              type="button"
              label="Закрыть"
              slate
              faded
              @click="runDialog.close()"
            />
            <Button
              v-if="selectedRun?.state === 'scheduled'"
              type="button"
              label="Удалить из расписания"
              ruby
              ghost
              :disabled="locked"
              @click="askDeleteRun(selectedRun)"
            />
          </div>
        </template>
      </Dialog>
      <Dialog
        ref="deleteRunDialog"
        title="Удалить публикацию из расписания?"
        type="alert"
        description="Удаляется только выбранное задание. Исходный пост и история сохранятся. Сообщения из Telegram не удаляются."
        confirm-button-label="Удалить из расписания"
        cancel-button-label="Оставить"
        :is-loading="saving"
        :disable-confirm-button="locked || !runToDelete"
        @confirm="cancelRun"
      >
        <Banner v-if="runDeleteError" color="ruby" role="alert">
          {{ runDeleteError }}
        </Banner>
      </Dialog>
      <Dialog
        ref="clientDialog"
        title="Добавить клиента"
        description="Лимит нельзя уменьшить ниже уже занятых публикаций."
        :disable-confirm-button="
          locked ||
          !clientForm.source ||
          !clientForm.group ||
          !clientForm.name.trim() ||
          Number(clientForm.slots) < 0
        "
        confirm-button-label="Добавить"
        @confirm="saveClient"
      >
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">Telegram-аккаунт</label>
            <TelegramAccountComboBox
              v-model="clientForm.source"
              :options="sourceOptions"
              placeholder="Выберите аккаунт"
              aria-label="Telegram-аккаунт"
              @update:model-value="loadGroups"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">Группа</label>
            <ComboBox
              v-model="clientForm.group"
              :options="groupOptions"
              placeholder="Выберите группу"
              aria-label="Группа"
            />
          </div>
          <Input
            v-model="clientForm.name"
            label="Название клиента"
            :maxlength="120"
          /><Input v-model="clientForm.username" label="@username" /><Input
            v-model="clientForm.slots"
            type="number"
            min="0"
            max="1000000"
            label="Оплачено публикаций"
          /><TextArea
            v-model="clientForm.notes"
            label="Примечание"
            :max-length="1000"
            resize
          />
        </div>
      </Dialog>
    </template>
  </SettingsLayout>
</template>
